import { Skeleton, Stack } from "@mui/material";
import FullScreenInit from "components/FullScreenInit";
import VirtualTourHeader from "components/VirtualTourHeader";
import { usePtcldScriptContext } from "contexts/PtcldScriptContext";
import { useRef, useEffect, useMemo } from "react";
import PointCloudView from "../PointCloudView";
import { useGetOutputPointcloud } from "api/output/queries/useGetOutputPointcloud";
import PCLDViewerHeader from "containers/PointCloudViewer/components/PointCloudViewerHeader";
import { getHTTPFileUrlFromS3Path, getHTTPUrlFromS3Path } from "utils/outputs";
import { useLoading } from "contexts/LoadingContext";
import { toast, Bounce } from "react-toastify";
import { useGetOutputQuery } from "api/output/queries/useGetOutputQuery";
import { useGetVirtualTourReferenceMeasurements } from "api/virtual-tour/queries/useGetVirtualTourReferenceMeasurements";
import { useGetScanTransformationQuery } from "api/scan/queries/useGetScanTransformationQuery";
import { getMiniMapSize, STATE_SELECTION } from "utils/potree";
import { ViewerMode } from "utils/misc";
import { useSearchParams } from "react-router-dom";

interface PointCloudScanProps {
  firstOutputId: string | undefined;
  secondOutputId: string | undefined;
}

function PointCloudViewerContent() {
  const {
    scriptsLoaded,
    scriptError,
    width,
    setIfcLoading,
    error,
    setError,
    setIsLock,
    setIsSplit,
    setIsOverlay,
    setIsIfcAvailable,
    setIsObjAvailable,
  } = usePtcldScriptContext();

  const potreeContainerDiv = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Potree.Viewer | null>(null);
  const {
    loading: pointcloudLoading,
    setLoading: setPointcloudLoading,
    setProgress: setLoadingProgression,
    progress: loadingProgression,
    blurryEffect,
    setBlurryEffect,
    ptcldOutputId1: firstOutputId,
    ptcldOutputId2: secondOutputId,
    vtourOutputId1,
    scan1,
    scan2,
    scan1Data,
  } = useLoading();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectionQuery = searchParams.get("selection");
  const secondType = searchParams.get("secondType");

  // console.log("--------", firstOutputId, secondOutputId);

  const {
    data: pointCloudData,
    isLoading: dataLoading,
    error: dataError,
  } = useGetOutputPointcloud(firstOutputId);

  const {
    data: pointCloudData2,
    isLoading: dataLoading2,
    error: dataError2,
  } = useGetOutputPointcloud(secondOutputId);
  const {
    data: vtouroutput,
    isLoading: outputLoading,
    error: outputError,
  } = useGetOutputQuery(vtourOutputId1);
  // console.log("outs", pointCloudData, pointCloudData2);
  const { data: output } = useGetOutputQuery(firstOutputId);
  const { data: scanTransformation1 } = useGetScanTransformationQuery(
    scan1 || ""
  );
  const { data: scanTransformation2 } = useGetScanTransformationQuery(
    scan2 || ""
  );
  const { data: referenceMeasurements } =
    useGetVirtualTourReferenceMeasurements(vtouroutput?.virtualTour?.id || "");
  const scaleFactor = useMemo(() => {
    if (!referenceMeasurements || referenceMeasurements.length === 0) return 0;

    const avgScaleFactor = referenceMeasurements.reduce(
      (prev, current) => prev + (current.metadata?.scaleFactor ?? 0),
      0
    );

    return avgScaleFactor / referenceMeasurements.length;
  }, [referenceMeasurements]);

  const outputUrl = output?.s3Path
    ? getHTTPUrlFromS3Path(output.s3Path).replace(
        "pointcloud/",
        "virtualtour/"
      ) + "vtour/"
    : "";
  const transformJsonUrl = outputUrl + "transforms_sparse_octree_textured.json";
  const getTransformJson = async () =>
    await (await fetch(transformJsonUrl)).json();
  const thumbnailUrl = outputUrl + "thumbnail.png";

  const objUrl = useMemo(() => {
    return scan1Data?.referenceFloorPlanAlignedObjURL;
  }, [scan1Data]);
  const floorImageUrl = useMemo(() => {
    return scan1Data?.referenceFloorPlanAlignedImageURL;
  }, [scan1Data]);

  // const {
  //   data: projectScanTransformation,
  //   isLoading: projectScanTransformationLoading,
  //   error: projectScanTransformationError,
  // } = useGetScanTransformationRelativeToProjectQuery(scanId);

  // console.log(projectScanTransformation);
  let matrix1: number[][] = [];
  useEffect(() => {
    const viewerElem = potreeContainerDiv.current;
    if (!blurryEffect) {
      setPointcloudLoading(true);
    }
    let pointcloud1: Potree.PointCloud | null = null;
    let pointcloud2: Potree.PointCloud | null = null;
    if (scriptsLoaded && pointCloudData && pointCloudData2) {
      // setPointcloudLoading(true); // earlier doing this but creating fluctation
      const Potree = window.Potree;
      const proj4 = window.proj4;
      if (!Potree) {
        setError("Potree library not found.");
        console.error("Potree library not found.");
        setPointcloudLoading(false);
        return;
      }
      if (viewerElem) {
        viewerRef.current = new Potree.Viewer(viewerElem);

        let matrix2: number[][] = [];
        if (scanTransformation1) {
          matrix1 = scanTransformation1.scanWrtWorld;
          console.log("matrix1", matrix1);
        }
        if (scanTransformation2) {
          matrix2 = scanTransformation2.scanWrtWorld;
        }

        const Image360 = async (
          coordinatesUrl: string,
          imageUrl: string,
          scene: number = 1
        ) => {
          try {
            if (viewerRef.current) {
              proj4.defs(
                "WGS84",
                "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs"
              );
              proj4.defs("pointcloud", viewerRef.current.getProjection());
              const transform = proj4("WGS84", "pointcloud");

              const params = {
                transform: transform,
              };

              // Load 360 images
              Potree.Images360Loader.load(
                coordinatesUrl,
                viewerRef.current,
                imageUrl,
                params
              ).then((images) => {
                console.log("Loaded 360 images");
                if (viewerRef.current) {
                  if (scene === 1) {
                    viewerRef.current.scene.add360Images(images);
                  } else {
                    viewerRef.current.scene2.add360Images(images);
                  }
                }
              });
            }
          } catch (err) {
            console.error("Error in run function:", err);
          }
        };

        const OrientedImage = async (
          coordinatesUrl: string,
          imageUrls: { [key: string]: string | null },
          xmlfile: string,
          scene: number = 1
        ) => {
          try {
            // Load oriented images
            if (viewerRef.current) {
              Potree.OrientedImageLoader.load(
                xmlfile,
                coordinatesUrl,
                viewerRef.current,
                imageUrls
              ).then((images) => {
                if (viewerRef.current) {
                  if (scene === 1) {
                    viewerRef.current.scene.addOrientedImages(images);
                  } else {
                    viewerRef.current.scene2.addOrientedImages(images);
                  }
                }
              });
            }
          } catch (err) {
            console.error("Error in run function:", err);
          }
        };

        try {
          if (viewerRef.current) {
            viewerRef.current.setEDLEnabled(true);
            viewerRef.current.setFOV(60);
            viewerRef.current.setPointBudget(1 * 1000 * 1000);
            viewerRef.current.setClipTask(Potree.ClipTask.HIGHLIGHT);
            viewerRef.current.loadSettingsFromURL();
            viewerRef.current.setBackground("black");

            viewerRef.current.setControls(viewerRef.current.orbitControls);
            setIsSplit(true);
            setIsLock(true);
            setIsOverlay(false);
            viewerRef.current.splitPaneResize(width);
            viewerRef.current.splitPane();
            viewerRef.current.splitPaneOverlay(false);

            viewerRef.current.loadGUI(() => {
              if (viewerRef.current) {
                viewerRef.current.setLanguage("en");
                const menuAppearance =
                  document.getElementById("menu_appearance");
                if (menuAppearance && menuAppearance.nextElementSibling) {
                  const sibling =
                    menuAppearance.nextElementSibling as HTMLElement;
                  sibling.style.display = "block";
                } else {
                  console.error("Next sibling of #menu_appearance not found.");
                }
              }
            });

            const url = getHTTPFileUrlFromS3Path(pointCloudData.metadata);
            const hierarchyUrl = getHTTPFileUrlFromS3Path(
              pointCloudData.hierarchy
            );
            const octreeUrl = getHTTPFileUrlFromS3Path(pointCloudData.octree);
            const otherPath = {
              octree: octreeUrl,
              hierarchy: hierarchyUrl,
            };

            Potree.loadPointCloud(url, otherPath, matrix1).then(
              (e) => {
                if (viewerRef.current) {
                  if (
                    objUrl &&
                    floorImageUrl &&
                    scan1Data?.scanTransformation?.referenceFloor?.metadata
                  ) {
                    viewerRef.current.setFloorPlan(
                      floorImageUrl,
                      {
                        x: scan1Data.scanTransformation?.referenceFloor
                          ?.metadata?.obj?.centerX,
                        y: scan1Data.scanTransformation?.referenceFloor
                          ?.metadata?.obj?.centerY,
                      },
                      scan1Data.scanTransformation?.referenceFloor?.metadata
                        ?.obj?.lengthX,
                      scan1Data.scanTransformation?.referenceFloor?.metadata
                        ?.obj?.lengthY
                    );
                    viewerRef.current.miniMapPosition(
                      undefined,
                      10,
                      getMiniMapSize(),
                      floorImageUrl,
                      {
                        x: scan1Data.scanTransformation?.referenceFloor
                          ?.metadata?.obj?.centerX,
                        y: scan1Data.scanTransformation?.referenceFloor
                          ?.metadata?.obj?.centerY,
                      },
                      scan1Data.scanTransformation?.referenceFloor?.metadata
                        ?.obj?.lengthX,
                      scan1Data.scanTransformation?.referenceFloor?.metadata
                        ?.obj?.lengthY
                    );
                    viewerRef.current.floorPlanEnabled = true;
                    viewerRef.current.thumbnailEnabled = false;
                  }
                  getTransformJson()
                    .then((data) => {
                      if ("thumbnail" in data) {
                        console.log("Thumbnail data exists:", data.thumbnail);
                        viewerRef.current?.setThumbnail(
                          thumbnailUrl,
                          {
                            x: data.thumbnail.center[0],
                            y: data.thumbnail.center[1],
                          },
                          data.thumbnail.width,
                          data.thumbnail.height
                        );
                        if (viewerRef.current && !viewerRef.current?.floorPlanEnabled) {
                          try {
                            viewerRef.current?.miniMapPosition(
                              undefined,
                              10,
                              getMiniMapSize(),
                              thumbnailUrl,
                              {
                                x: data.thumbnail.center[0],
                                y: data.thumbnail.center[1],
                              },
                              data.thumbnail.width,
                              data.thumbnail.height
                            );
                            viewerRef.current.thumbnailEnabled = true;
                            viewerRef.current.floorPlanEnabled = false;
                          } catch (e) {
                            console.error(
                              "Error setting minimap image in Potree:",
                              e
                            );
                          }
                        }
                      } else {
                        console.log("No thumbnail data found in JSON");
                      }
                    })
                    .catch((error) =>
                      console.error("Error fetching transform JSON:", error)
                    );
                  pointcloud1 = e.pointcloud;
                  pointcloud1.name = "ptcld";
                  const material = pointcloud1.material;

                  material.activeAttributeName = "rgba";
                  material.minSize = 2;
                  material.pointSizeType = Potree.PointSizeType.FIXED;
                  if (scaleFactor && scaleFactor !== 0) {
                    viewerRef.current.setExternalMeasureScale(scaleFactor);
                  }
                  viewerRef.current.scene.addPointCloud(pointcloud1);
                  viewerRef.current.fitToScreen();

                  // 360 images check
                  if (
                    pointCloudData.coordinates360 &&
                    pointCloudData.vtImage360Path
                  ) {
                    const coordinates360path = getHTTPFileUrlFromS3Path(
                      pointCloudData.coordinates360
                    );
                    const images360path = getHTTPUrlFromS3Path(
                      pointCloudData.vtImage360Path
                    );
                    Image360(coordinates360path, images360path);
                  }

                  // Oriented/simple images check
                  if (
                    pointCloudData.coordinatesOriented &&
                    pointCloudData.xmlfile &&
                    pointCloudData.imageOriented
                  ) {
                    OrientedImage(
                      pointCloudData.coordinatesOriented,
                      pointCloudData.imageOriented,
                      pointCloudData.xmlfile
                    );
                  }

                  // viewerRef.current.mapView.showSources(false);
                  setLoadingProgression(100);
                }
              },
              (e) => {
                console.error("ERROR loading point cloud:", e);
                setPointcloudLoading(false);
              }
            );

            if (pointCloudData.ifc && secondType !== "ifc") {
              setIfcLoading(true);
              const ifc = new Potree.IFC(viewerRef.current);
              const ifcUrl = getHTTPFileUrlFromS3Path(pointCloudData.ifc);
              ifc.load(ifcUrl, viewerRef.current.scene, "ifc").then(() => {
                // mesh: unknown
                {
                  setIfcLoading(false);
                }
              });
            }

            const url2 = getHTTPFileUrlFromS3Path(pointCloudData2.metadata);
            const hierarchyUrl2 = getHTTPFileUrlFromS3Path(
              pointCloudData2.hierarchy
            );
            const octreeUrl2 = getHTTPFileUrlFromS3Path(pointCloudData2.octree);
            const otherPath2 = {
              octree: octreeUrl2,
              hierarchy: hierarchyUrl2,
            };

            Potree.loadPointCloud(url2, otherPath2, matrix2).then(
              (e) => {
                if (viewerRef.current) {
                  pointcloud2 = e.pointcloud;
                  pointcloud2.name = "ptcld-split";
                  const material = pointcloud2.material;

                  material.activeAttributeName = "rgba";
                  material.minSize = 2;
                  material.pointSizeType = Potree.PointSizeType.FIXED;

                  viewerRef.current.scene2.addPointCloud(pointcloud2);
                  
                  if (secondType === "ifc") {
                    pointcloud2.visible = false;
                  }
                  
                  viewerRef.current.fitToScreen();

                  // 360 images check
                  if (
                    pointCloudData2.coordinates360 &&
                    pointCloudData2.vtImage360Path
                  ) {
                    const coordinates360path2 = getHTTPFileUrlFromS3Path(
                      pointCloudData2.coordinates360
                    );
                    const images360path2 = getHTTPUrlFromS3Path(
                      pointCloudData2.vtImage360Path
                    );
                    Image360(coordinates360path2, images360path2, 2);
                  }

                  // Oriented/simple images check
                  if (
                    pointCloudData2.coordinatesOriented &&
                    pointCloudData2.xmlfile &&
                    pointCloudData2.imageOriented
                  ) {
                    OrientedImage(
                      pointCloudData2.coordinatesOriented,
                      pointCloudData2.imageOriented,
                      pointCloudData2.xmlfile,
                      2
                    );
                  }

                  // viewerRef.current.mapView.showSources(false);
                }
              },
              (e) => {
                console.error("ERROR loading point cloud:", e);
              }
            );

            if (pointCloudData2.ifc && secondType === "ifc") {
              const ifc = new Potree.IFC(viewerRef.current);
              const ifcUrl2 = getHTTPFileUrlFromS3Path(pointCloudData2.ifc);
              ifc.load(ifcUrl2, viewerRef.current.scene2, "ifc-split");
              // .then(() => {
              //   // mesh: unknown
              //   {
              //   }
              // });
            }

            // Potree.loadPointCloud(url, otherPath).then(
            //   (e) => {
            //     if (viewerRef.current) {
            //       const pointcloud = e.pointcloud;
            //       pointcloud.name = "split-ptcld";
            //       pointcloud.visible = false;
            //       const material = pointcloud.material;

            //       material.activeAttributeName = "rgba";
            //       material.minSize = 2;
            //       material.pointSizeType = Potree.PointSizeType.FIXED;

            //       viewerRef.current.scene2.addPointCloud(pointcloud);
            //       viewerRef.current.fitToScreen();
            //       viewerRef.current.mapView.showSources(false);
            //       setPointcloudLoading(false);
            //     }
            //   },
            //   (e) => {
            //     console.error("ERROR loading point cloud:", e);
            //     setPointcloudLoading(false);
            //   }
            // );
          }
        } catch (err) {
          setError("Failed to initialize Potree viewer.");
          console.error("Error loading point cloud:", err);
          setPointcloudLoading(false);
        }
      }
    }
    return () => {
      document
        .querySelectorAll(".sp-container, .ui-slider, .i18n-container")
        .forEach((element) => {
          element.remove();
        });

      if (pointcloud1 && viewerRef.current) {
        viewerRef.current?.renderer.dispose();
        Potree.cleanupPotree(pointcloud1 as Potree.PointCloud);
        if (pointcloud2) {
          Potree.cleanupPotree(pointcloud2 as Potree.PointCloud);
        }
      }

      if (viewerRef.current) {
        console.log("Viewer ref exists, clearing it", viewerRef.current);
        viewerRef.current.renderer.clear();
        viewerRef.current = null;

        const profileWindow = document.getElementById("profile_window");
        if (profileWindow && profileWindow.parentNode) {
          profileWindow.parentNode.removeChild(profileWindow);
        }
      }
    };
  }, [pointCloudData, scriptsLoaded, pointCloudData2]);

  if (dataError || error || scriptError) {
    return toast.error(error || scriptError || dataError?.message, {
      position: "bottom-left",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "dark",
      transition: Bounce,
    });
  }

  return (
    <Stack height="100%" direction="column">
      <PointCloudView
        potreeContainerDiv={potreeContainerDiv}
        viewerRef={viewerRef}
      />
    </Stack>
  );
}

export default PointCloudViewerContent;
